import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { useSidebarStore } from "@/stores/sidebar";
import { safeLocalStorage } from "@/stores/terminal-dock";

/**
 * The docked Ruby panel's UI state (fs#2534): the assistant panel that opens
 * on the left, between the rail and the routed page.
 *
 * A per-viewer PREFERENCE, persisted like the terminal dock — whether the
 * panel is open, how wide it is, and the rail state to restore on close.
 * Never conversation content: the chat itself lives on the server and in
 * ruby-ui's own hooks. Storage goes through the dock's never-throwing
 * wrapper, so a browser that blocks localStorage costs the panel its memory,
 * never the page its rendering. Sign-out closes the panel (`useLogout`).
 *
 * The panel is FlowStack's only Ruby surface: the rail's Ruby row toggles
 * it, and ruby-ui's floating `RubyWidget` is not mounted (fs#2540).
 *
 * **Opening collapses the rail; closing restores it.** The rule lives here
 * rather than in a component, so it holds whoever opens or closes the panel:
 * the rail goes to its icon-only mode while the panel is open, and comes
 * back to whatever it was before — expanded or collapsed — when it closes.
 */

/** The panel's width, in pixels, when first opened. */
export const RUBY_PANEL_DEFAULT_PX = 400;
/** The narrowest the panel may be dragged, in pixels. */
export const RUBY_PANEL_MIN_PX = 320;
/** The widest the panel may be dragged, in pixels. */
export const RUBY_PANEL_MAX_PX = 720;

export interface RubyPanelState {
  open: boolean;
  /** The panel's width, `RUBY_PANEL_MIN_PX`..`RUBY_PANEL_MAX_PX`. */
  widthPx: number;
  /**
   * The rail's `collapsed` value from just before the panel opened, which
   * {@link RubyPanelState.close} puts back. Null while the panel is closed.
   */
  railWasCollapsed: boolean | null;
  /**
   * Set by an open in this page session, cleared by the panel once it has
   * moved focus to its composer. Never persisted, so a panel restored open
   * by a reload does not steal focus.
   */
  focusRequested: boolean;
  /** Read and clear {@link RubyPanelState.focusRequested}. */
  consumeFocusRequest: () => boolean;
  /** The rail's Ruby entry: open the panel if it is closed, else close it. */
  toggle: () => void;
  /** Open the panel, collapsing the rail. A no-op when it is already open. */
  openPanel: () => void;
  /** Close the panel and restore the rail. A no-op when it is already closed. */
  close: () => void;
  /** A dragged or keyed width; clamped to the bounds. */
  setWidthPx: (px: number) => void;
  /** Back to the defaults, without touching the rail — tests. */
  reset: () => void;
}

const DEFAULTS = {
  open: false,
  widthPx: RUBY_PANEL_DEFAULT_PX,
  railWasCollapsed: null as boolean | null,
  focusRequested: false,
};

function clampWidth(value: number): number {
  if (!Number.isFinite(value)) return RUBY_PANEL_DEFAULT_PX;
  return Math.round(Math.min(RUBY_PANEL_MAX_PX, Math.max(RUBY_PANEL_MIN_PX, value)));
}

type Persisted = Pick<RubyPanelState, "open" | "widthPx" | "railWasCollapsed">;

/**
 * A rehydrated entry, checked field by field: localStorage is user-editable,
 * so anything that does not parse falls back to its default.
 */
export function asPersistedRubyPanel(value: unknown): Persisted {
  const raw = (typeof value === "object" && value !== null ? value : {}) as Record<string, unknown>;
  const open = raw.open === true;
  return {
    open,
    widthPx: typeof raw.widthPx === "number" ? clampWidth(raw.widthPx) : RUBY_PANEL_DEFAULT_PX,
    // Only an open panel has a rail state to restore.
    railWasCollapsed:
      open && typeof raw.railWasCollapsed === "boolean" ? raw.railWasCollapsed : null,
  };
}

export const useRubyPanelStore = create<RubyPanelState>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,
      toggle: () => (get().open ? get().close() : get().openPanel()),
      openPanel: () => {
        if (get().open) return;
        const sidebar = useSidebarStore.getState();
        set({ open: true, railWasCollapsed: sidebar.collapsed, focusRequested: true });
        sidebar.setCollapsed(true);
      },
      consumeFocusRequest: () => {
        const requested = get().focusRequested;
        if (requested) set({ focusRequested: false });
        return requested;
      },
      close: () => {
        const { open, railWasCollapsed } = get();
        if (!open) return;
        set({ open: false, railWasCollapsed: null, focusRequested: false });
        // An entry without a recorded rail state (hand-edited storage) leaves
        // the rail as it is rather than guessing.
        if (railWasCollapsed !== null) useSidebarStore.getState().setCollapsed(railWasCollapsed);
      },
      setWidthPx: (px) => set({ widthPx: clampWidth(px) }),
      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: "flowstack-ruby-panel",
      version: 1,
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (s): Persisted => ({
        open: s.open,
        widthPx: s.widthPx,
        railWasCollapsed: s.railWasCollapsed,
      }),
      merge: (persisted, current) => ({ ...current, ...asPersistedRubyPanel(persisted) }),
    }
  )
);
